/**
 * geocode.js — geocodifica indirizzi tramite Nominatim (OpenStreetMap), gratuito
 * e senza bisogno di API key.
 *
 * USATO IN DUE PUNTI:
 *  1. AdminDashboard.js, quando un'azienda salva/modifica indirizzo+città nelle
 *     impostazioni: geocodifica UNA VOLTA e salva lat/lng insieme al resto dei
 *     dati aziendali (companies/{id}/settings/azienda). Così la ricerca per
 *     vicinanza e la mappa in /cerca non devono geocodificare nulla al volo.
 *  2. SearchCompanies.js (/cerca), quando il cliente inserisce un indirizzo per
 *     cercare i noleggi più vicini.
 *
 * NOTA SUI LIMITI DI NOMINATIM: è un servizio gratuito con una policy d'uso
 * (https://operations.osmfoundation.org/policies/nominatim/) che chiede un
 * ritmo massimo di ~1 richiesta al secondo e nessun uso massivo/automatizzato.
 * Per questi due casi d'uso (geocodifica occasionale lato admin, ricerca
 * occasionale lato cliente) va benissimo così com'è. Se in futuro il traffico
 * di ricerca crescesse molto, valutare un proxy server-side con cache, o il
 * passaggio a Google Geocoding API (a pagamento).
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

/**
 * Geocodifica un indirizzo libero (via, città, ecc.) in coordinate lat/lng.
 * Restituisce null se non trova nulla o in caso di errore di rete.
 */
export async function geocodeAddress(query) {
  const q = (query || '').trim();
  if (!q) return null;

  try {
    const url = `${NOMINATIM_URL}?format=json&limit=1&addressdetails=0&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'it' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
      const { lat, lon } = data[0];
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lon);
      if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
        return { lat: latNum, lng: lngNum };
      }
    }
  } catch (error) {
    console.error('Errore geocodifica indirizzo:', error);
  }
  return null;
}

/**
 * Distanza in km tra due coordinate (formula haversine). Calcolo interamente
 * lato client, nessuna chiamata di rete: usato per ordinare i risultati di
 * ricerca per vicinanza una volta note le coordinate di tutti i noleggi e
 * quelle dell'indirizzo cercato dal cliente.
 */
export function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371; // raggio terrestre in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
